import React, { useState } from 'react';
import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Upload, Tooltip, Row, Col, message, Spin } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { blogAdd } from '../../../utils/API';

interface FormValues {
  title: string;
  description: string;
  image: UploadFile[];
  content: {
    section: string;
    description: string;
    image: UploadFile[];
  }[];
}

const AddBlog: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // Loading state

  const onFinish = async (values: FormValues) => {
    setIsLoading(true); // Start loading
    try {
      const formData = new FormData();

      formData.append('title', values.title);
      formData.append('description', values.description);

      // Handle the main image upload
      if (values.image && values.image[0]?.originFileObj) {
        formData.append('image', values.image[0].originFileObj);
      }

      // Append nested fields for content
      if (Array.isArray(values.content)) {
        values.content.forEach((contentItem, index) => {
          formData.append(`content[${index}][section]`, contentItem.section);
          formData.append(`content[${index}][description]`, contentItem.description);

          // Append content image
          if (contentItem.image && contentItem.image[0]?.originFileObj) {
            formData.append(`content[${index}][image]`, contentItem.image[0].originFileObj);
          }
        });
      }

      const response = await blogAdd(formData);
      if (response.data.message) {
        message.success(response.data.message);
        navigate('/backend/blog');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to create blog post.');
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <>
   <Row className="m-2" align="middle" justify="space-between">
   <Col>
     <h2 className="text-2xl font-bold text-left">Add Blog</h2>
   </Col>
   <Col>
     <Link to="/backend/blog">
       <button className="d-flex justify-content-center align-items-center w-full border btn-brand">
         <FaArrowLeft className="me-2" />
         <div className="ms-2">Back</div>
       </button>
     </Link>
   </Col>
 </Row>
 

      <div className="flex justify-center min-h-screen items-center bg-gray-100">
        <div className="p-6 rounded-lg shadow-lg w-full">
          <Spin spinning={isLoading} tip="Submitting..."> {/* Spinner wraps the form */}
            <Form
              form={form}
              name="dynamic_form_nest_item"
              onFinish={onFinish}
              autoComplete="off"
              initialValues={{
                content: [{ section: '', description: '', image: [] }],
              }}
            >
              <Row gutter={24}>
                <Col span={12}>
                  <Row>
                    <Col className="p-2" xs={18} sm={18} md={18}>
                      <label className="font-bold pb-2">Title</label>
                      <Form.Item
                        name="title"
                        rules={[{ required: true, message: 'Missing Title' }]}
                      >
                        <Input size="large" className="rounded border border-1" />
                      </Form.Item>
                    </Col>
                    <Col xs={6} sm={6} md={6}>
                      <label className="font-bold pb-2">Main Image</label>
                      <Form.Item
                        name="image"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                        rules={[{ required: true, message: 'Missing upload' }]}
                      >
                        <Upload
                          listType="picture-card"
                          beforeUpload={() => false}
                          showUploadList={{ showPreviewIcon: true }}
                        >
                          <UploadOutlined />
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={12}>
                  <Col xs={24} sm={12} md={12}>
                    <label className="font-bold pb-2">Description</label>
                    <Form.Item
                      name="description"
                      rules={[{ required: true, message: 'Missing Description' }]}
                    >
                      <Input.TextArea placeholder="Description" cols={24} rows={6} />
                    </Form.Item>
                  </Col>
                </Col>
              </Row>

              <Form.List name="content">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, fieldKey, ...restField }) => (
                      <div key={key} className="bg-gray-50 p-4 rounded-lg shadow-sm space-y-4">
                        <Row gutter={[16, 16]}>
                          <Col xs={24} sm={12} md={7}>
                            <label className="font-bold pb-2">Heading</label>
                            <Form.Item
                              {...restField}
                              name={[name, 'section']}
                              rules={[{ required: true, message: 'Missing heading' }]}
                            >
                              <Input.TextArea placeholder="Heading" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={7}>
                            <label className="font-bold pb-2">Image</label>
                            <Form.Item
                              {...restField}
                              name={[name, 'image']}
                              valuePropName="fileList"
                              getValueFromEvent={(e) =>
                                Array.isArray(e) ? e : e?.fileList
                              }
                              rules={[{ required: true, message: 'Missing upload' }]}
                            >
                              <Upload
                                listType="picture-card"
                                beforeUpload={() => false}
                                showUploadList={{ showPreviewIcon: true }}
                              >
                                <UploadOutlined />
                              </Upload>
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={7}>
                            <label className="font-bold pb-2">Description</label>
                            <Form.Item
                              {...restField}
                              name={[name, 'description']}
                              rules={[{ required: true, message: 'Missing description' }]}
                            >
                              <ReactQuill
                                theme="snow"
                                className="rounded border border-1"
                                style={{ height: '150px' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col
                            xs={24}
                            sm={12}
                            md={3}
                            className="flex justify-end items-center"
                          >
                            <Tooltip title="Remove this field">
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                                className="text-red-500 text-xl cursor-pointer"
                              />
                            </Tooltip>
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Form.Item>
                      <Button
                        type="dashed"
                        className="mt-4"
                        onClick={() => add()}
                        block
                        icon={<PlusOutlined />}
                      >
                        Add field
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
              <Form.Item>
                <button type="submit" className="btn-brand" disabled={isLoading}>
                  {isLoading ? 'Submitting...' : 'Submit'}
                </button>
              </Form.Item>
            </Form>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default AddBlog;
